<script>
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { navigate as goto } from 'svelte-routing';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate, faPlus, faPencil, faTrash, faSquarePlus, faTrashCan, faHistory, faCircleXmark } from '@fortawesome/pro-light-svg-icons';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import DeviceModal from '$lib/DeviceModal.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAuditLogAccess, hasAccessUiAction } from '$utils/permissionUtils.js';

  // Use local API endpoints instead of direct API with key 
  const API_BASE_URL = '/api/proxy';

  let devices = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let searchText = '';
  let sortField = 'name';
  let sortOrder = 'asc';
  
  // Modal state
  let showCreateModal = false;
  let showEditModal = false;
  let showDeleteModal = false;
  let showAuditLog = false;
  let selectedDevice = null;
  let auditLogDevice = null;
  let readingMetrics = [];
  let deviceReadingTypes = [];
  let displayUnits = [];
  let isLoading = false;

  onMount(async () => {
    //selectedStudy.set(null);
    selectedSite.set(null);
    selectedParticipant.set(null);
    
    isLoading = true;
    try {
      await Promise.all([
        fetchDevices(1),
        fetchLookups()
      ]);
    } finally {
      isLoading = false;
    }
  });

  async function fetchLookups() {
    try {
      // Fetch reading metrics
      const metricsRes = await fetch(`${API_BASE_URL}/devices/readingmetrics?active=true&page=1&pageSize=999`);
      
      if (metricsRes.ok) {
        const metricsData = await metricsRes.json();
        readingMetrics = metricsData.data || [];
      }
      
      // Fetch device reading types (DRT lookup)
      const typesRes = await fetch(`${API_BASE_URL}/lookups/DRT`);
      
      if (typesRes.ok) {
        const typesData = await typesRes.json();
        deviceReadingTypes = typesData.data || [];
      }
      
      // Fetch display units (DURDISPUNT lookup)
      const unitsRes = await fetch(`${API_BASE_URL}/lookups/DURDISPUNT`);
      
      if (unitsRes.ok) {
        const unitsData = await unitsRes.json();
        displayUnits = unitsData.data || [];
      }
    } catch (err) {
      console.error('Error fetching lookups:', err);
    }
  }

  async function fetchDevices(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/devices?page=${page}&pageSize=${limit}&sortField=${sortField}&sortOrder=${sortOrder}`;

      if (searchText) {
        url += `&searchText=${encodeURIComponent(searchText)}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch devices');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;
      devices = responseData.data;
    } catch (err) {
      console.error('Error fetching devices:', err);
      devices = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    fetchDevices(currentPage);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchDevices(newPage);
    }
  }

  function handleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortOrder = 'asc';
    }
    fetchDevices(1);
  }
  
  function openCreateModal() {
    selectedDevice = {
      name: '',
      code: '',
      description: '',
      manufacturer: '',
      model: '',
      deviceReadingTypeGUID: '',
      active: true,
      defaultReadingDuration: 0,
      defaultReadingFrequency: 0,
      defaultReadingCount: 0,
      defaultReadingThreshold: 0,
      url: '',
      technology: '',
      defaultReadingDurationDisplayUnitGUID: '',
      defaultReadingFrequencyDisplayUnitGUID: '',
      deviceReadingPurposeGUID: ''
    };
    showCreateModal = true;
  }
  
  function openEditModal(device) {
    selectedDevice = { ...device };
    showEditModal = true;
  }
  
  function openDeleteModal(device) {
    selectedDevice = device;
    showDeleteModal = true;
  }
  
  function handleAuditLogClick(device) {
    auditLogDevice = device;
    showAuditLog = true;
  }
  
  async function handleCreateDevice(device) {
    try {
      // Check permission before creating
      if (!hasAccessUiAction('devices.add', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to create device');
        return;
      }
      
      isLoading = true;
      const res = await fetch(`${API_BASE_URL}/devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(device),
      });
      
      if (!res.ok) {
        throw new Error('Failed to create device');
      }
      
      showCreateModal = false;
      await fetchDevices(1);
    } catch (err) {
      console.error('Error creating device:', err);
    } finally {
      isLoading = false;
    }
  }
  
  async function handleUpdateDevice(device) {
    try {
      // Check permission before updating
      if (!hasAccessUiAction('devices.edit', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to update device');
        return;
      }
      
      isLoading = true;
      const res = await fetch(`${API_BASE_URL}/devices/${device._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(device),
      });
      
      if (!res.ok) {
        throw new Error('Failed to update device');
      }
      
      showEditModal = false;
      await fetchDevices(currentPage);
    } catch (err) {
      console.error('Error updating device:', err);
    } finally {
      isLoading = false;
    }
  }
  
  async function handleDeleteDevice() {
    try {
      // Check permission before deleting
      if (!hasAccessUiAction('devices.delete', get(userRoles), get(studyProperties), get(userReadOnly))) {
        console.error('Insufficient permissions to delete device');
        return;
      }
      
      isLoading = true;
      const res = await fetch(`${API_BASE_URL}/devices/${selectedDevice._id}`, {
        method: 'DELETE'
      });
      
      if (!res.ok) {
        throw new Error('Failed to delete device');
      }
      
      showDeleteModal = false;
      await fetchDevices(currentPage);
    } catch (err) {
      console.error('Error deleting device:', err);
    } finally {
      isLoading = false;
    }
  }
  
  function handleClearSearch() {
    searchText = '';
    fetchDevices(1);
  }
  
  // Add computed property for edit permissions
  $: canAddDevices = hasAccessUiAction('devices.add', $userRoles, $studyProperties, $userReadOnly);
  $: canEditDevices = hasAccessUiAction('devices.edit', $userRoles, $studyProperties, $userReadOnly);
  $: canDeleteDevices = hasAccessUiAction('devices.delete', $userRoles, $studyProperties, $userReadOnly);
  $: canViewAuditLogs = hasAuditLogAccess($userRoles, $studyProperties, $userReadOnly);
</script>

<StudyHeader />

<FeatureInfo 
  title="Devices Management" 
  description="This page allows you to manage all devices in the system. You can search, create, edit, and delete devices. Each device entry includes details such as name, code, manufacturer, model, and active status. View audit logs to track changes to device settings."
  marginBottom="mb-0"
/>

<StudyDetails showOptionsMenu={false} />

<Toolbar title="Devices" itemCountTitle="Devices" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="relative">
      <input
        type="text"
        placeholder="Search"
        bind:value={searchText}
        on:input={() => fetchDevices(1)}
        class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
      />
      {#if searchText}
        <button
          on:click={handleClearSearch}
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Clear search"
        >
          <FontAwesomeIcon icon={faCircleXmark} />
        </button>
      {/if}
    </div>
    <button 
      on:click={openCreateModal} 
      class="pagination mx-1 p-0" 
      disabled={!canAddDevices}
      class:opacity-50={!canAddDevices}
      title={!canAddDevices ? 'Insufficient permissions' : 'Add new device'}
    >
      <FontAwesomeIcon icon={faSquarePlus} />
    </button>
    <div class="text-white dark:text-white">
      <button on:click={handleRefresh} class="pagination mx-1 p-0" disabled={isLoading}>
        <FontAwesomeIcon icon={faRotate} spin={isLoading} />
      </button>
    </div>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('name')}>
          Name {sortField === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('code')}>
          Code {sortField === 'code' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th
          class="px-4 py-2 text-left bold cursor-pointer"
          on:click={() => handleSort('manufacturer')}
        >
          Manufacturer {sortField === 'manufacturer' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('model')}>
          Model {sortField === 'model' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left bold w-1/4">Description</th>
        <th
          class="px-4 py-2 text-left bold cursor-pointer"
          on:click={() => handleSort('technology')}
        >
          Technology {sortField === 'technology' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left bold">Status</th>
        <th class="w-px px-4 py-2 text-left bold">Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each devices as device, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2">{device.name}</td>
          <td class="px-4 py-2">{device.code}</td>
          <td class="px-4 py-2">{device.manufacturer}</td>
          <td class="px-4 py-2">{device.model}</td>
          <td class="px-4 py-2 w-1/4">{device.description}</td>
          <td class="px-4 py-2">{device.technology ? device.technology : ''}</td>
          <td class="px-4 py-2">
            <span
              class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
              class:text-green-700={device.active}
              class:ring-green-700={device.active}
              class:text-red-600={!device.active}
              class:ring-red-600={!device.active}
            >
              {device.active ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td class="w-px px-4 py-2">
            <div class="flex items-center space-x-2">
              <button 
                on:click={() => openEditModal(device)} 
                class="pagination mx-1 p-0" 
                title="Edit"
                disabled={!canEditDevices}
                class:opacity-50={!canEditDevices}
              >
                <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
              </button>
              <button
                on:click={() => handleAuditLogClick(device)}
                class="pagination mx-1 p-0"
                title="View audit logs"
                disabled={!canViewAuditLogs}
                class:opacity-50={!canViewAuditLogs}
              >
                <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
              </button>
              <button 
                on:click={() => openDeleteModal(device)} 
                class="pagination mx-1 p-0" 
                title="Delete"
                disabled={!canDeleteDevices}
                class:opacity-50={!canDeleteDevices}
              >
                <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
              </button>
            </div>
          </td>
        </tr>
      {/each}
      {#if devices.length === 0}
        <tr>
          <td colspan="8" class="text-center text-gray-500 dark:text-slate-400 mt-10 py-10">
            No devices found
          </td>
        </tr>
      {/if}
    </tbody>
  </table>
</div>

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>

{#if showCreateModal}
  <DeviceModal
    device={selectedDevice}
    {readingMetrics}
    {deviceReadingTypes}
    {displayUnits}
    onClose={() => (showCreateModal = false)}
    onSave={handleCreateDevice}
    title="Create Device"
  />
{/if}

{#if showEditModal}
  <DeviceModal
    device={selectedDevice}
    {readingMetrics}
    {deviceReadingTypes}
    {displayUnits}
    onClose={() => (showEditModal = false)}
    onSave={handleUpdateDevice}
    title="Edit Device"
  />
{/if}

{#if showDeleteModal}
  <ConfirmationModal
    showConfirmation={true}
    message={`Are you sure you want to delete the device "${selectedDevice?.name}"? This action cannot be undone.`}
    confirmPhrase="delete"
    on:confirm={handleDeleteDevice}
    on:close={() => (showDeleteModal = false)}
  />
{/if}

{#if showAuditLog}
  <AuditLogModal
    showModal={showAuditLog}
    documentId={auditLogDevice?._id}
    collection="Device"
    title={`Audit Logs - ${auditLogDevice?.name || 'Device'}`}
    on:close={() => (showAuditLog = false)}
  />
{/if}
